import { useCommentProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/comments/useCommentProvider';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainCard/Common/comments/comments.module.scss';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { CustomizedButton, CustomizedConfirmDeleteModal, CustomizedRoundIcon } from 'components/common';
import { MuiIcon } from 'components/common/muiIcon/muiIcon';
import { ProfileImage } from 'components/common/ProfileImage/profileImage';
import RichTextArea from 'components/common/richTextArea/richTextArea';
import moment from 'moment-timezone';
import constants from 'components/common/Constants/constants';
import Icon from 'components/common/Icons/icon';
import { isStringHasRealContent } from 'utilitys/helpers/general';
import { QueryResponse as CardResponse } from 'api/queries/getMainCard.query';

interface Props {
    id: number;
    isVirtual: boolean;
    assigneeOptions: { text: string; value: string; url: string }[];
    card?: CardResponse;
    queryKeys?: string[];
}

export const CommentsMobile = ({ id, isVirtual, assigneeOptions, queryKeys = [] }: Props) => {
    const {
        cancelAddCommentMode,
        commentData,
        commentError,
        commentId,
        commentsEditorStatus,
        confirmDeleteComment,
        createComment,
        deleteCommentModalShow,
        email,
        firstName,
        handleCommentDelete,
        handleEditorDescription,
        image,
        isLoading,
        lastName,
        removeCommentId,
        resetCommentsEditorStatus,
        setAddCommentMode,
        setCommentData,
        setCommentError,
        setCommentId,
        setDeleteCommentModalShow,
        sortedComments,
        updateComment,
        username,
    } = useCommentProvider({ id, isVirtual, queryKeys });

    return (
        <div className={classes.commentsContainer} data-track-id={`comments-card`}>
            {isLoading && <OverlayWithSpinner />}
            <div className={classes.titleWrapper}>
                <div className={classes.leftSection}>
                    <div className={classes.title}>Comments:</div>
                </div>
                {commentsEditorStatus.isOpen && (
                    <div className={classes.actionButtons}>
                        <CustomizedRoundIcon onClick={cancelAddCommentMode} size={'4rem'} backGroundColor="red">
                            <MuiIcon icon={'cancel'} fontSize={'4rem'} color={'white'} />
                        </CustomizedRoundIcon>
                        <CustomizedRoundIcon
                            onClick={createComment}
                            size={'4rem'}
                            style={{ border: '1px solid #2C7D90' }}
                        >
                            <MuiIcon icon={'send'} fontSize={'3rem'} color={'#2C7D90'} />
                        </CustomizedRoundIcon>
                    </div>
                )}
            </div>
            <div className={classes.addCommentContainer}>
                <div className={classes.profileImageWrapper}>
                    <ProfileImage fullName={`${firstName} ${lastName}`} email={email} imageName={image || ''} />
                </div>
                {!commentsEditorStatus.isOpen ? (
                    <div className={classes.addCommentAndQuickCommentsContainer}>
                        <button data-testid={'add-comment'} className={classes.addCommentInput} onClick={() => setAddCommentMode('')}>
                            Add a comment...
                        </button>

                        <div className={classes.quickCommentsContainer}>
                            <CustomizedButton
                                className={classes.quickCommentText}
                                color={'secondary'}
                                onClick={() => setAddCommentMode('We’re on track')}
                                size={'small'}
                            >
                                👍 We’re on track
                            </CustomizedButton>
                            <CustomizedButton
                                className={classes.quickCommentText}
                                color={'secondary'}
                                onClick={() => setAddCommentMode('Waiting on an update')}
                                size={'small'}
                            >
                                ⌛️ Waiting on an update
                            </CustomizedButton>
                            <CustomizedButton
                                className={classes.quickCommentText}
                                color={'secondary'}
                                onClick={() => setAddCommentMode('Update status')}
                                size={'small'}
                            >
                                📢 Update status
                            </CustomizedButton>
                        </div>
                    </div>
                ) : (
                    <div className={classes.addCommentWrapper}>
                        <div data-testid={'comments-text-area'} className={classes.textArea}>
                            <RichTextArea
                                onEditorDescription={handleEditorDescription}
                                existingData={commentData.comment}
                                editorContentHeight={180}
                                isAutoFocus={true}
                                isError={commentError}
                                mention={{
                                    separator: ' ',
                                    trigger: '@',
                                    suggestions: assigneeOptions,
                                }}
                                placeholder={
                                    'Enter your comments here. \nType @ to mention another user and notify them about it.'
                                }
                            />
                        </div>
                    </div>
                )}
            </div>

            {sortedComments.map((item) => (
                <div key={item.updatedDate} className={classes.commentWrapper}>
                    <ProfileImage
                        fullName={`${item.assignee.firstName} ${item.assignee.lastName}`}
                        email={item.assignee.userName}
                        imageName={item.assignee.imageName || ''}
                    />
                    <div data-testid={'comments-row'} className={classes.wrapper}>
                        <div className={classes.iconsWrapper}>
                            <div className={classes.iconsHeader}>
                                <span>{`${item.firstName} ${item.lastName}`}</span>
                                <span>
                                    {moment(item.updatedDate).format(constants.formats.date.fullDateWithMilliSeconds)}
                                </span>

                                {username === item.userName && (
                                    <div className={classes.actionIcons}>
                                        <CustomizedRoundIcon
                                            size={'2.6rem'}
                                            onClick={() => {
                                                setCommentId(item.commentId);
                                                setCommentError(false);
                                                setCommentData({ ...commentData, id: item.commentId, comment: '' });
                                                resetCommentsEditorStatus();
                                            }}
                                        >
                                            <Icon data-testid={'edit'} name={'edit-user-profile'} size="1.6rem" />
                                        </CustomizedRoundIcon>
                                        <CustomizedRoundIcon
                                            size={'2.6rem'}
                                            onClick={() => {
                                                confirmDeleteComment(item.commentId);
                                            }}
                                        >
                                            <Icon data-testid={'delete'} name={'delete-icon'} size="1.6rem" />
                                        </CustomizedRoundIcon>
                                    </div>
                                )}
                            </div>

                            {commentId === item.commentId && (
                                <div className={classes.actionButtons}>
                                    <CustomizedButton
                                        color={'secondary'}
                                        onClick={() => {
                                            setCommentId(false);
                                            setCommentData({ ...commentData, id: null, comment: '' });
                                        }}
                                    >
                                        Cancel
                                    </CustomizedButton>
                                    <CustomizedButton
                                        color={'primary'}
                                        onClick={() => {
                                            updateComment(item.commentId);
                                        }}
                                    >
                                        Update
                                    </CustomizedButton>
                                </div>
                            )}
                        </div>

                        <div data-testid={'comment-text'} className={classes.commentText}>
                            {commentId === item.commentId ? (
                                <div className={classes.textArea}>
                                    <RichTextArea
                                        onEditorDescription={handleEditorDescription}
                                        existingData={
                                            commentData.id === item.commentId &&
                                            !isStringHasRealContent(commentData.comment)
                                                ? commentData.comment
                                                : item.comment
                                        }
                                        editorContentHeight={160}
                                        isAutoFocus={true}
                                        isError={commentError}
                                        mention={{
                                            separator: ' ',
                                            trigger: '@',
                                            suggestions: assigneeOptions,
                                        }}
                                    />
                                </div>
                            ) : (
                                <span dangerouslySetInnerHTML={{ __html: item.comment }} />
                            )}
                        </div>
                    </div>
                </div>
            ))}

            {removeCommentId && (
                <CustomizedConfirmDeleteModal
                    isShowModal={deleteCommentModalShow}
                    hideModal={() => {
                        setDeleteCommentModalShow(false);
                    }}
                    text={'Are you sure you want to delete this Comment?'}
                    onConfirm={() => handleCommentDelete(removeCommentId)}
                />
            )}
        </div>
    );
};
